import React from 'react';

import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { ThirdPartyConfigsBootstrap } from '../../../../../../../../common/constants/schemas/3p-config';
import { WORKSPACE_NAME_MAX_LENGTH } from '../../../../../../../../common/ui/workspace-name-metadata';
import { serveFederatedSlackExperiment } from '../../../../../../../../common/utils/experiments/experiment-utils';
import { SLACK_INTEGRATION_ARI } from '../../../../../../../../common/utils/filters/integration-aris';

import type { threadTitle_AtlassianSearchPage$key } from './__generated__/threadTitle_AtlassianSearchPage.graphql';
import { i18n } from './messages';

const USER_DISPLAY_LIMIT = 3;
const MPDM_USER_DISPLAY_LIMIT = 4;
interface ThreadTitleProps {
	node: threadTitle_AtlassianSearchPage$key;
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
}

type ThirdPartyUser = {
	id: string;
	name?: string | null;
};

const dedupUsers = (
	userMap: Record<string, { thirdPartyUser: ThirdPartyUser; index: number }>,
	user: ThirdPartyUser,
	index: number,
) => {
	if (!userMap[user.id]) {
		userMap[user.id] = { thirdPartyUser: user, index };
	}
	return userMap;
};

const getTitleMessageValues = (
	intl: IntlShape,
	users: (ThirdPartyUser | undefined | null)[] = [],
	limit: number = USER_DISPLAY_LIMIT,
) => {
	const { formatMessage } = intl;
	const uniqueUsers = users
		.filter((thirdPartyUser): thirdPartyUser is ThirdPartyUser =>
			Boolean(thirdPartyUser?.name?.trim()),
		)
		.reduce(dedupUsers, {});
	const validContributors = Object.values(uniqueUsers)
		.sort((a, b) => a.index - b.index)
		.map((entry) => entry?.thirdPartyUser?.name || formatMessage(i18n.externalPersonLabel))
		.map((name) => name?.split(' ')[0]);

	const externalPersonLabel = formatMessage(i18n.externalPersonLabel);
	const externalPersonCount = validContributors.filter(
		(name) => name === externalPersonLabel,
	).length;

	let adjustedContributors = validContributors;
	if (externalPersonCount > 1) {
		adjustedContributors = validContributors.filter((name) => name !== externalPersonLabel);
		adjustedContributors.unshift(formatMessage(i18n.externalPeopleLabel));
	}

	const contributorCount = adjustedContributors.length;
	const namedContributors = adjustedContributors.slice(0, limit);
	const overflowContributorCount = adjustedContributors.slice(limit).length;

	return { contributorCount, namedContributors, overflowContributorCount };
};

const truncateWorkspaceName = (name: string): string => {
	if (name.length <= WORKSPACE_NAME_MAX_LENGTH) {
		return name;
	}
	return `${name.slice(0, WORKSPACE_NAME_MAX_LENGTH)}...`;
};

const getRegularThreadTitleValues = (data: any, intl: IntlShape, formatMessage: any) => {
	const linkedEntities = data.linkedEntities || [];
	const threadUsers = [data, ...linkedEntities].map(
		(linkedMessage) =>
			linkedMessage?.entity?.createdBy?.thirdPartyUser || linkedMessage?.initialContributors?.[0],
	);

	const { contributorCount, namedContributors, overflowContributorCount } = getTitleMessageValues(
		intl,
		threadUsers,
	);

	const [name1, name2, name3] = namedContributors;
	const rawWorkspaceName = data?.workspaceName;
	const workspaceName = rawWorkspaceName ? truncateWorkspaceName(rawWorkspaceName) : null;
	const useSlackChannelName =
		data?.integrationId === SLACK_INTEGRATION_ARI &&
		fg('ai_3p_slack_eg_workspace_metadata_enabled');
	const fallbackChannelName = useSlackChannelName
		? data?.containerName
		: workspaceName || data?.containerName;
	const channelName =
		data?.entity?.container?.displayName ||
		fallbackChannelName ||
		formatMessage(i18n.unknownChannel);

	return {
		values: {
			contributorCount,
			name1,
			name2,
			name3,
			overflowContributorCount,
			channelName: channelName,
		},
		titleMessage: i18n.threadTitleWithOthers,
	};
};

const buildMpdmTitleValues = (
	namedContributors: (string | undefined)[],
	totalOthersCount: number,
) => {
	const [name1, name2, name3, name4] = namedContributors;
	const hasOthers = totalOthersCount > 0;
	return {
		values: {
			knownNamesCount: namedContributors.length,
			name1,
			name2,
			name3,
			name4,
			totalOthersCount,
		},
		titleMessage: hasOthers
			? totalOthersCount === 1
				? i18n.federatedGroupDirectMessageWithOneOther
				: i18n.federatedGroupDirectMessageWithMultipleOthers
			: i18n.federatedGroupDirectMessage,
	};
};

export const ThreadTitle = ({ node, thirdPartyConfigs }: ThreadTitleProps): React.JSX.Element => {
	const intl = useIntl();
	const { formatMessage } = intl;
	let values = {};
	let titleMessage = i18n.groupChatTitle;

	const data = useFragment(
		graphql`
			fragment threadTitle_AtlassianSearchPage on SearchResultGraphDocument {
				containerName @optIn(to: "SearchPerformanceImprovementChanges")
				workspaceName @optIn(to: "SearchPerformanceImprovementChanges")
				integrationId
				initialContributors {
					id
					name
				}
				allContributors {
					id
					name
				}
				entity {
					... on ExternalMessage {
						createdBy {
							thirdPartyUser {
								id
								name
							}
						}
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalConversation {
								displayName
								members {
									thirdPartyUser {
										id
										name
									}
								}
								type
							}
						}
					}
				}
				linkedEntities {
					initialContributors {
						id
						name
					}
					entity {
						... on ExternalMessage {
							createdBy {
								thirdPartyUser {
									id
									name
								}
							}
							container @optIn(to: "ExternalDataDepotQueryV2") {
								... on ExternalConversation {
									members {
										thirdPartyUser {
											id
											name
										}
									}
									type
								}
							}
						}
					}
				}
			}
		`,
		node,
	);

	const isSlackFederatedSearchEnabled = serveFederatedSlackExperiment(
		undefined,
		undefined,
		{ fireExperimentExposure: false },
		thirdPartyConfigs,
	);

	const isSlackIntegration = data.integrationId === SLACK_INTEGRATION_ARI;

	if (data.containerName != null && isSlackFederatedSearchEnabled && isSlackIntegration) {
		const groupChatParticipants = data?.allContributors;

		// direct message
		if (data.containerName === '') {
			const nonNullParticipants =
				groupChatParticipants?.filter((participant) => participant?.name) || [];
			const { namedContributors } = getTitleMessageValues(intl, nonNullParticipants);
			const [name1, name2] = namedContributors;

			if (nonNullParticipants.length === 1) {
				values = { name1 };
				titleMessage = i18n.directMessageSingleUser;
			} else {
				values = { name1, name2 };
				titleMessage = i18n.directMessageTwoUsers;
			}
		}
		// group direct message
		else if (data.containerName && /^mpdm-.+--.*-\d+$/.test(data.containerName)) {
			// Extract number of people from containerName (format: mpdm-name1--name2--name3-1)
			const totalPeopleCount = (data.containerName.match(/--/g) || []).length + 1;
			const nonNullParticipants =
				groupChatParticipants?.filter((participant) => participant?.name) || [];
			const otherCount = totalPeopleCount - nonNullParticipants.length;

			const { namedContributors: mpdmNamedContributors } = getTitleMessageValues(
				intl,
				nonNullParticipants,
				MPDM_USER_DISPLAY_LIMIT,
			);
			if (mpdmNamedContributors.length > 0) {
				// Calculate total others (remaining known names + unknown people)
				const totalOthersCount =
					nonNullParticipants.length - mpdmNamedContributors.length + otherCount;
				const result = buildMpdmTitleValues(mpdmNamedContributors, totalOthersCount);
				values = result.values;
				titleMessage = result.titleMessage;
			} else if (fg('slack_mpdm_title_fallback_from_container_name')) {
				// No contributor enrichment available — fall back to Slack handles parsed
				// directly from containerName so the user still sees identifiable identifiers.
				// containerName format: mpdm-handle1--handle2--...--handleN-N
				const handlesFromContainerName = data.containerName
					.replace(/^mpdm-/, '')
					.replace(/-\d+$/, '')
					.split('--')
					.filter((handle) => handle.length > 0);

				if (handlesFromContainerName.length > 0) {
					const namedHandles = handlesFromContainerName.slice(0, MPDM_USER_DISPLAY_LIMIT);
					const totalOthersCount = handlesFromContainerName.length - namedHandles.length;
					const result = buildMpdmTitleValues(namedHandles, totalOthersCount);
					values = result.values;
					titleMessage = result.titleMessage;
				} else {
					// containerName itself failed to parse — keep the legacy "N people" rendering.
					values = { totalPeopleCount };
					titleMessage = i18n.groupDirectMessageUnknownNames;
				}
			} else {
				// Feature gate off — preserve the legacy "N people" fallback.
				values = { totalPeopleCount };
				titleMessage = i18n.groupDirectMessageUnknownNames;
			}
		}
		// fallback to regular thread title logic
		else {
			const result = getRegularThreadTitleValues(data, intl, formatMessage);
			values = result.values;
			titleMessage = result.titleMessage;
		}
	} else if (data?.entity?.container?.type === 'GROUP_MEETING') {
		const displayName = data?.entity?.container?.displayName?.trim();
		if (displayName) {
			// Named GROUP_MEETING — show "Bob in Create some random chat data" (no #)
			const result = getRegularThreadTitleValues(data, intl, formatMessage);
			values = { ...result.values, channelName: displayName };
			titleMessage = i18n.groupMeetingTitle;
		} else {
			// Unnamed GROUP_MEETING — show names only, don't show "unknown-channel"
			const groupChatParticipants =
				data?.entity?.container?.members?.map((member) => member?.thirdPartyUser) ?? [];
			const { contributorCount, namedContributors, overflowContributorCount } =
				getTitleMessageValues(intl, groupChatParticipants);
			const [name1, name2, name3] = namedContributors;
			values = { contributorCount, name1, name2, name3, overflowContributorCount };
			titleMessage = i18n.groupChatTitle;
		}
	} else if (
		data?.entity?.container?.type === 'DIRECT_MESSAGE' ||
		data?.entity?.container?.type === 'GROUP_DIRECT_MESSAGE'
	) {
		const groupChatParticipants =
			data?.entity?.container?.members?.map((member) => member?.thirdPartyUser) ?? [];

		const { contributorCount, namedContributors, overflowContributorCount } = getTitleMessageValues(
			intl,
			groupChatParticipants,
		);

		const [name1, name2, name3] = namedContributors;

		values = {
			contributorCount,
			name1,
			name2,
			name3,
			overflowContributorCount,
		};
		titleMessage = i18n.groupChatTitle;
	} else {
		const result = getRegularThreadTitleValues(data, intl, formatMessage);
		values = result.values;
		titleMessage = result.titleMessage;
	}

	return <FormattedMessage {...titleMessage} values={values} />;
};
